import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useEnterExitAnimation } from '../../util/useEnterExitAnimation.js';
import { useEventListener } from '../../util/useEventListener.js';
import { useLockBodyScroll } from '../../util/useLockBodyScroll.js';
import { Box } from '../Box/Box.js';
import { FocusTrap } from '../FocusTrap/FocusTrap.js';
import { Overlay } from '../Modal/Overlay.js';
import { useLocaleContext } from '../../contexts/session/Locale.js';
import { mapOverlayTransform, defaultProps } from './FullScreenSheet.mappers.js';

var _excluded = ["visible", "onClose", "autoFocus", "backgroundColor", "zIndex", "children"];
var OVERLAY_TRANSITION = '0.3s ease-out';
var ANIMATION_FALL_BACK_TIME_MS = 300;

/**
 * FullScreenSheet displays additional information, actions, or workflows over the current page without redirecting to a new URL.
 */
var FullScreenSheet = function FullScreenSheet(props) {
  var visible = props.visible,
    onClose = props.onClose,
    autoFocus = props.autoFocus,
    backgroundColor = props.backgroundColor,
    zIndex = props.zIndex,
    children = props.children,
    rest = _objectWithoutProperties(props, _excluded);
  var _useLocaleContext = useLocaleContext(),
    direction = _useLocaleContext.direction;
  var lockBodyScroll = useLockBodyScroll();
  var _useEnterExitAnimatio = useEnterExitAnimation({
      initialProps: {
        opacity: 0
      },
      enterProps: {
        opacity: 1
      },
      animationOn: visible,
      transition: OVERLAY_TRANSITION,
      animationFallBackTimeMs: ANIMATION_FALL_BACK_TIME_MS
    }),
    transitionPropsOverlay = _useEnterExitAnimatio.transitionProps,
    shouldRenderOverlay = _useEnterExitAnimatio.shouldRender;
  var _useEnterExitAnimatio2 = useEnterExitAnimation({
      initialProps: {
        transform: mapOverlayTransform(direction)
      },
      enterProps: {
        transform: 'translate3d(0, 0, 0)'
      },
      animationOn: visible,
      transition: OVERLAY_TRANSITION,
      animationFallBackTimeMs: ANIMATION_FALL_BACK_TIME_MS
    }),
    transitionProps = _useEnterExitAnimatio2.transitionProps,
    shouldRender = _useEnterExitAnimatio2.shouldRender;
  var handleEscKey = React.useCallback(function (event) {
    if (event instanceof KeyboardEvent && event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  useEventListener({
    handler: handleEscKey,
    eventName: 'keydown',
    attached: visible
  });
  return shouldRenderOverlay ? /*#__PURE__*/React.createElement(Overlay, _extends({
    height: "100dvh",
    zIndex: zIndex,
    onClick: onClose
  }, transitionPropsOverlay), /*#__PURE__*/React.createElement(FocusTrap, {
    display: "flex",
    open: visible,
    autoFocus: autoFocus
  }, shouldRender ?
  /*#__PURE__*/
  // eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-props -- @agoda/agoda-a11y/no-incorrect-props[3e6d51dc-37cd-4329-bf91-bff7370a0ad2]
  React.createElement(Box, _extends({
    ref: lockBodyScroll,
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    width: "100%",
    height: "100dvh",
    overflowY: "auto",
    role: "dialog",
    "aria-modal": "true",
    top: 0,
    bottom: 0,
    start: 0,
    end: 0,
    onClick: function onClick(event) {
      return event.stopPropagation();
    },
    backgroundColor: backgroundColor
  }, rest, transitionProps), children) : null)) : null;
};
FullScreenSheet.defaultProps = defaultProps;
var FullScreenSheetMemoized = /*#__PURE__*/React.memo(FullScreenSheet);
FullScreenSheetMemoized.displayName = 'FullScreenSheet';

export { FullScreenSheet as BaseFullScreenSheet, FullScreenSheetMemoized as FullScreenSheet };
//# sourceMappingURL=FullScreenSheet.js.map
